import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, BRAND } from "../lib/config";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { CreditCard, Loader2, AlertCircle, Clock, CalendarDays, XCircle,
         FileText, ChevronDown, ChevronUp } from "lucide-react";
import PayNowInline from "./PayNowInline";

/**
 * iter125 — Consolidated Payment Due section shown at the TOP of the
 * customer dashboard. Replaces per-card "Pay Now" buttons.
 *
 * - Hidden entirely when the customer owes nothing.
 * - Each row is either "Unpaid" (no checkout started) or "Payment Pending"
 *   (a Stripe checkout session is open, customer bounced before completing).
 * - "Continue Payment" resumes the same open session; if none, opens a
 *   fresh one via /pay-now (which also reuses open sessions server-side).
 * - "Cancel Payment" is only shown for Payment Pending rows and expires
 *   the Stripe session so the customer can start fresh later.
 *
 * Props:
 *   onRefresh — called after any successful pay/cancel action so the
 *               dashboard can reload bookings + this section.
 */
export default function PaymentDueSection({ onRefresh }) {
  const [state, setState] = useState({ items: [], total_due: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [busyKind, setBusyKind] = useState(null); // "continue" | "cancel"
  const [expanded, setExpanded] = useState(true);
  // iter220 — Deep-link support. When the customer arrives from a
  // "Remind to Pay" email/SMS the URL is
  // `/dashboard?pay=<booking_id>`. We auto-expand that row and scroll
  // it into view so the invoice is visible with the Payment
  // Authorization disclosure ready.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkPayId = searchParams.get("pay") || "";

  const load = () => {
    setLoading(true);
    api.get("/me/payment-due")
      .then(({ data }) => setState(data || { items: [], total_due: 0, count: 0 }))
      .catch(() => setState({ items: [], total_due: 0, count: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Nothing due → render nothing at all (per product spec).
  if (loading) return null;
  if (!state.items?.length) return null;

  const pendingCount = state.items.filter((i) => i.state === "payment_pending").length;

  const handleContinue = async (item) => {
    setBusyId(item.booking_id); setBusyKind("continue");
    try {
      // Prefer resuming the exact open session if the server already has one
      // (single tap → straight to Stripe, no round-trip).
      if (item.state === "payment_pending" && item.checkout_url) {
        window.location.assign(item.checkout_url);
        return;
      }
      // Otherwise ask the server to create/reuse and hand us the URL.
      const { data } = await api.post(`/bookings/${item.booking_id}/pay-now`);
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.assign(data.url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (typeof detail === "object" && detail?.message)
        ? detail.message
        : (typeof detail === "string" ? detail : "Couldn't open checkout. Please try again.");
      toast.error(msg);
      setBusyId(null); setBusyKind(null);
    }
  };

  const handleCancel = async (item) => {
    if (!window.confirm(
      `Cancel the in-progress payment for ${item.service_name}? The invoice will still be due — you can start a new payment any time.`
    )) return;
    setBusyId(item.booking_id); setBusyKind("cancel");
    try {
      await api.post(`/bookings/${item.booking_id}/cancel-payment`);
      toast.success("Payment cancelled. You can resume any time.");
      load();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel that payment. Please try again.");
    } finally {
      setBusyId(null); setBusyKind(null);
    }
  };

  return (
    <div
      className="mt-6 rounded-3xl border-2 border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-amber-50/50 overflow-hidden"
      data-testid="payment-due-section"
    >
      {/* Header — single unified summary */}
      <div className="px-4 sm:px-5 py-4 border-b border-rose-100 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
          <CreditCard className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-lg sm:text-xl font-extrabold tracking-tight text-gray-900 font-[Outfit] leading-tight">
            Payment Due
          </div>
          <div className="text-xs text-gray-600 leading-tight">
            {state.count === 1 ? "1 invoice" : `${state.count} invoices`}
            {pendingCount > 0 && ` · ${pendingCount} in progress`}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-rose-700">
            Total due
          </div>
          <div className="text-2xl font-extrabold text-rose-800 tabular-nums leading-none"
               data-testid="payment-due-total">
            ${state.total_due.toFixed(2)}
          </div>
        </div>
        {state.items.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse" : "Expand"}
            className="ml-1 w-8 h-8 rounded-full border border-rose-200 bg-white text-rose-700 flex items-center justify-center hover:bg-rose-50"
            data-testid="payment-due-toggle"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
      </div>

      {expanded && (
        <ul className="divide-y divide-rose-100" data-testid="payment-due-list">
          {state.items.map((item) => (
            <PaymentDueRow
              key={item.booking_id}
              item={item}
              busy={busyId === item.booking_id}
              busyKind={busyKind}
              onContinue={() => handleContinue(item)}
              onCancel={() => handleCancel(item)}
              onRefresh={() => { load(); onRefresh?.(); }}
              autoExpand={deepLinkPayId === item.booking_id}
              onAutoExpandConsumed={() => {
                // Remove ?pay=... from the URL so a refresh doesn't
                // re-trigger and to keep the URL clean.
                const next = new URLSearchParams(searchParams);
                next.delete("pay");
                setSearchParams(next, { replace: true });
              }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PaymentDueRow({ item, busy, busyKind, onContinue, onCancel, onRefresh,
                          autoExpand = false, onAutoExpandConsumed }) {
  const isPending = item.state === "payment_pending";
  const canPayOnline = item.can_pay_online;
  // iter219 — When the customer taps Pay Now on the compact row, expand
  // the row inline to reveal the full rich payment UI (tip picker +
  // Payment Authorization disclosure + big CTA). The disclosure MUST be
  // visible BEFORE the customer actually pays.
  const [showPayUI, setShowPayUI] = useState(false);
  const rowRef = useRef(null);

  // iter220 — Deep-link auto-expand. When the customer lands from a
  // Remind-to-Pay email/SMS (`/dashboard?pay=<booking_id>`), auto-open
  // this specific row and scroll it into view once.
  useEffect(() => {
    if (autoExpand && canPayOnline && !showPayUI) {
      setShowPayUI(true);
      // Small delay so PayNowInline mounts, then scroll into view.
      setTimeout(() => {
        try {
          rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch { /* older browsers */ }
      }, 120);
      onAutoExpandConsumed?.();
    }
  }, [autoExpand, canPayOnline]);

  const badgeTone = isPending
    ? "bg-blue-100 text-blue-800 border-blue-200"
    : "bg-amber-100 text-amber-800 border-amber-200";
  const badgeLabel = isPending ? "Payment Pending" : "Unpaid";

  const smsHref = `sms:${BRAND.phone.replace(/[^\d+]/g, "")}?body=${encodeURIComponent(
    `Hi JDSN, I have a question about invoice ${item.booking_id}.`,
  )}`;

  // Shim: PayNowInline expects a full booking-like object. The
  // /me/payment-due list gives us all fields we need to render the
  // pre-payment disclosure UI. `status='completed'` + `payment_status=
  // 'unpaid'` unlocks the render path inside PayNowInline.
  const bookingShim = {
    booking_id: item.booking_id,
    status: "completed",
    payment_status: isPending ? "payment_pending" : "unpaid",
    payment_method: item.payment_method || "stripe",
    payment_method_label: item.payment_method_label,
    frequency: item.frequency || "one_time",
    price: Number(item.amount || 0),
  };

  return (
    <li ref={rowRef} className="px-4 sm:px-5 py-3.5 flex flex-col gap-3"
        data-testid={`payment-due-row-${item.booking_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Service info */}
        <div className="flex-1 min-w-0 flex items-start gap-3">
          <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] rounded-full border px-2 py-0.5 flex-shrink-0 mt-0.5 ${badgeTone}`}
                data-testid={`payment-due-status-${item.booking_id}`}>
            {isPending && <Clock className="w-3 h-3" />}
            {!isPending && <AlertCircle className="w-3 h-3" />}
            {badgeLabel}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-extrabold text-gray-900 truncate">
              {item.service_name}
            </div>
            <div className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
              <CalendarDays className="w-3 h-3" /> {item.service_date || "—"}
              <span className="ml-2 text-[10px] font-mono text-gray-400">
                #{item.booking_id.slice(-6)}
              </span>
            </div>
          </div>
        </div>

        {/* Amount */}
        <div className="text-right sm:min-w-[80px] sm:mx-2">
          <div className="text-lg font-extrabold text-rose-800 tabular-nums leading-none"
               data-testid={`payment-due-amount-${item.booking_id}`}>
            ${item.amount.toFixed(2)}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {canPayOnline ? (
            <>
              <Button
                onClick={() => {
                  // iter219 — Payment Pending row that already has an
                  // open Stripe session: keep the fast path (resume
                  // straight to Stripe, one tap). For fresh Unpaid
                  // invoices, EXPAND the row inline to show the
                  // Payment Authorization disclosure BEFORE redirect.
                  if (isPending && item.checkout_url) {
                    onContinue();
                    return;
                  }
                  setShowPayUI((v) => !v);
                }}
                disabled={busy}
                size="sm"
                className="rounded-full h-10 px-4 bg-[#16A34A] hover:bg-[#15803D] text-white font-extrabold shadow-sm"
                data-testid={`payment-due-continue-${item.booking_id}`}
              >
                {busy && busyKind === "continue" ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening…</>
                ) : (
                  <><CreditCard className="w-4 h-4 mr-1.5" />
                    {isPending && item.checkout_url
                      ? "Continue Payment"
                      : (showPayUI ? "Hide payment options" : "Pay Now")}
                  </>
                )}
              </Button>
              {isPending && (
                <Button
                  onClick={onCancel}
                  disabled={busy}
                  size="sm"
                  variant="outline"
                  className="rounded-full h-10 px-3 border-rose-200 text-rose-700 hover:bg-rose-50 font-extrabold"
                  data-testid={`payment-due-cancel-${item.booking_id}`}
                >
                  {busy && busyKind === "cancel" ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <><XCircle className="w-4 h-4 mr-1" /> Cancel</>
                  )}
                </Button>
              )}
            </>
          ) : (
            // Non-Stripe payment methods: show the chosen method + text-us for support.
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.12em] rounded-full border bg-gray-50 text-gray-700 border-gray-200 px-2 py-1"
                    data-testid={`payment-due-method-${item.booking_id}`}>
                Pay via {item.payment_method_label || item.payment_method || "—"}
              </span>
              <a
                href={smsHref}
                className="inline-flex items-center gap-1 px-3 h-9 rounded-full border border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50 text-emerald-800 text-xs font-extrabold"
                data-testid={`payment-due-contact-${item.booking_id}`}
              >
                Text Us
              </a>
            </div>
          )}
          <Link
            to={`/receipt/${item.booking_id}`}
            className="hidden sm:inline-flex items-center gap-1 px-2.5 h-9 rounded-full border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-600 text-xs font-extrabold"
            data-testid={`payment-due-invoice-${item.booking_id}`}
            title="View invoice"
          >
            <FileText className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* iter219 — Expanded rich payment UI: tip picker + Payment
          Authorization disclosure + big pill CTA. Shown when the
          customer clicks Pay Now, before the Stripe redirect. */}
      {showPayUI && canPayOnline && (
        <div className="mt-1 -mx-1 sm:-mx-2 rounded-3xl bg-white/60 p-1 sm:p-2 border border-emerald-100">
          <PayNowInline booking={bookingShim} onPaid={onRefresh} />
        </div>
      )}
    </li>
  );
}
